import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Typography } from '@mui/material'
import React from 'react'
import { useMutation } from 'react-query'
import { AdoptersClient } from '../../interactors/clients/AdoptersClient'
import { AnimalsClient } from '../../interactors/clients/AnimalsClient'
import {
  AdopterDto,
  AdoptionAttemptDto,
  AnimalAdoptionDtoOfferType,
  AnimalAdoptionDtoStatus,
  AnimalAdoptionDtoStep,
  AnimalDto,
} from '../../interactors/gen/backendClient'
import { useAccountsStore } from '../../store/AccountsStore'
import { useAnimalStore } from '../../store/AnimalStore'
import { useAdoptersStore } from '../../store/AdoptersStore'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { useFetchOrRefreshAdoptersOnMount } from '../../store/useFetchOrRefreshAdoptersOnMount'
import { readableAxiosError } from '../../utils/axios'
import { suspendConflictingAdoptionAttempts } from '../../utils/suspendConflictingAdoptionAttempts'
import { blue } from '../theme'
import { AddDocumentsStep } from './FastLaunchProcedureSteps/AddDocumentsStep'
import { AdoptionInfoStep } from './FastLaunchProcedureSteps/AdoptionInfoStep'
import { MatchAnimalStep } from './FastLaunchProcedureSteps/MatchAnimalStep'
import { SummaryStep } from './FastLaunchProcedureSteps/SummaryStep'
import { useStripeConnectStatus } from '../../utils/hooks/useStripeConnectStatus'
import { createAdoptionAttempt } from '../../domain/Adopters/createAdoptionAttempt'
import { GuestAdoptionLinkDto } from '../../interactors/clients/GuestAdoptionsClient'
import { GuestAdoptionLinkContent } from './GuestAdoption/GuestAdoptionLinkDialog'
import { StripeConnectRequiredStep } from './StripeConnectRequiredStep'

export type AdoptionContext = {
  offerType?: AnimalAdoptionDtoOfferType
  priceInEuros?: number
  hasDeposit?: boolean
  depositDate?: string
  depositAmount?: number
  depositStatus?: boolean
  prepareDocuments?: boolean
}

interface Props {
  adopter: AdopterDto | null
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  attemptId?: string
  providedAnimal?: AnimalDto
  noAdopterDelete?: boolean
  needNavigate?: boolean
  sendEmailToAdopter: boolean
  adoptionContext?: AdoptionContext
  initialAdoptionAttempt?: AdoptionAttemptDto
}

export interface ProcedureStepsFormParams {
  hasAnimal: boolean
  gavedCertificate: boolean
  gavedContract: boolean
  animalId: string
}

export const FastLaunchProcedureDialog: React.FC<Props> = ({
  open,
  setOpen,
  adopter,
  attemptId,
  providedAnimal,
  noAdopterDelete = false,
  needNavigate = false,
  sendEmailToAdopter,
  adoptionContext,
  initialAdoptionAttempt,
}) => {
  const shouldShowAdoptionInfoStep = !adoptionContext
  const getInitialStep = React.useCallback(() => {
    if (shouldShowAdoptionInfoStep) {
      return 1
    }
    return providedAnimal ? 3 : 2
  }, [providedAnimal, shouldShowAdoptionInfoStep])
  const [isLoading, setIsLoading] = React.useState(false)
  const [isClosing, setIsClosing] = React.useState(false)
  const [currentStep, setCurrentStep] = React.useState(getInitialStep)
  const [animal, setAnimal] = React.useState<AnimalDto | undefined>(providedAnimal)
  const [adoptionAttempt, setAdoptionAttempt] = React.useState<AdoptionAttemptDto | undefined>(initialAdoptionAttempt)
  const [guestAdoptionLink, setGuestAdoptionLink] = React.useState<GuestAdoptionLinkDto | null>(null)
  const [localAdoptionContext, setLocalAdoptionContext] = React.useState<AdoptionContext | undefined>(adoptionContext)

  /**
   * The offer and the price are often already filled on the animal sheet before the procedure is
   * started. Carrying them over means the first step confirms what was entered instead of asking
   * for it twice.
   */
  const adoptionInfoDefaults = React.useMemo<AdoptionContext | undefined>(() => {
    if (localAdoptionContext) {
      return localAdoptionContext
    }

    return providedAnimal
      ? { offerType: providedAnimal.adoption.offerType, priceInEuros: providedAnimal.adoption.priceInEuros }
      : undefined
  }, [localAdoptionContext, providedAnimal])

  useFetchOrRefreshAdoptersOnMount()
  const animalStore = useAnimalStore()
  const adoptersStore = useAdoptersStore()
  const accountsStore = useAccountsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const customer = accountsStore.connectedCustomer
  const isPremium = accountsStore.isPremium()
  const { status: stripeStatus, refresh: refreshStripeStatus } = useStripeConnectStatus(open)
  const [prepareDocuments, setPrepareDocuments] = React.useState(
    isPremium ? localAdoptionContext?.prepareDocuments ?? true : false
  )
  const shouldSkipDocuments = !prepareDocuments

  React.useEffect(() => {
    if (initialAdoptionAttempt) {
      setAdoptionAttempt(initialAdoptionAttempt)
    }
  }, [initialAdoptionAttempt])

  React.useEffect(() => {
    if (adoptionContext) {
      setLocalAdoptionContext(adoptionContext)
    }
  }, [adoptionContext])

  React.useEffect(() => {
    if (!isPremium) {
      setPrepareDocuments(false)
      return
    }
    if (localAdoptionContext?.prepareDocuments !== undefined) {
      setPrepareDocuments(localAdoptionContext.prepareDocuments)
      return
    }
    setPrepareDocuments(true)
  }, [localAdoptionContext?.prepareDocuments, isPremium])

  React.useEffect(() => {
    if (!adopter || !adoptionAttempt) return
    const withoutDocumentsSupport = !prepareDocuments
    if (adoptionAttempt.withoutDocumentsSupport === withoutDocumentsSupport) return

    const syncDocumentsSupport = async () => {
      const updatedAttempt = await AdoptersClient.updateAdoptionAttempt(adopter.id, {
        ...adoptionAttempt,
        withoutDocumentsSupport,
      })
      setAdoptionAttempt(updatedAttempt)
    }

    syncDocumentsSupport()
  }, [adopter, adoptionAttempt, prepareDocuments])

  const closeDialog = () => {
    if (currentStep === 4) {
      setOpen(false)
      setCurrentStep(getInitialStep())
      setAnimal(providedAnimal)
      setAdoptionAttempt(undefined)
      setGuestAdoptionLink(null)
      setGuestAdoptionLink(null)
      setIsLoading(false)
      setIsClosing(false)
      setLocalAdoptionContext(adoptionContext)
      setPrepareDocuments(isPremium ? adoptionContext?.prepareDocuments ?? true : false)
      // replace with a redirect to force refreshing the page
      window.location.href = `${window.location.origin}/suivi-adoptant/${adopter?.id}`
      return
    }
    setIsClosing(true)
  }

  const deleteAdopterAndCloseDialog = async () => {
    if (animal) {
      animal.adoption.status = AnimalAdoptionDtoStatus.Adoptable
      animal.adoption.adopterId = ''
      animal.adoption.step = undefined
      animal.adoption.adoptionDate = ''
      animal.adoption.exitDate = ''
      animal.adoption.vpaDate = ''
      await AnimalsClient.editAnimal(animal)
      animalStore.changeSelectedAnimal(animal.id)
    }
    if (adopter && !noAdopterDelete) {
      AdoptersClient.deleteAdopter(adopter.id).then(() => {
        setOpen(false)
      })
    }
    if (noAdopterDelete && adopter && adopter.adoptionAttempts) {
      adopter.adoptionAttempts = adopter.adoptionAttempts.filter(
        (attempt) => attempt.id !== ((adoptionAttempt?.id ?? attemptId) as string)
      )
      await AdoptersClient.editAccount(adopter)
    }
    setOpen(false)

    setCurrentStep(getInitialStep())
    setAnimal(providedAnimal)
    setAdoptionAttempt(undefined)
    setGuestAdoptionLink(null)
    setIsLoading(false)
    setIsClosing(false)
    setLocalAdoptionContext(adoptionContext)
    setPrepareDocuments(isPremium ? adoptionContext?.prepareDocuments ?? true : false)
    if (needNavigate) {
      window.location.href = `${window.location.origin}/suivi-adoptant/${adopter?.id}`
    }
  }

  const uploadAdoptionDocumentMutation = useMutation(
    async ({
      file,
      adopterId,
      adoptionAttemptId,
      type,
      deliveredByHand,
      isLocked,
      deliverLater,
      pdfTemplateKey,
    }: {
      file?: File | null
      adopterId: string
      adoptionAttemptId: string
      type: 'certificate' | 'contract'
      deliveredByHand?: boolean
      isLocked?: boolean
      deliverLater?: boolean
      pdfTemplateKey?: string
    }) => {
      const formData = new FormData()
      if (file) {
        formData.append('file', file)
      }

      if (deliveredByHand) {
        formData.append('deliveredByHand', 'true')
      }

      if (deliverLater) {
        formData.append('deliverLater', 'true')
      }

      if (isLocked) {
        formData.append('isLocked', 'true')
      }

      if (pdfTemplateKey) {
        formData.append('pdfTemplateKey', pdfTemplateKey)
        formData.append('deliveredByHand', 'false')
      }

      return await AdoptersClient.addOriginalDocumentToAdoptionAttempt(adopterId, adoptionAttemptId, formData, type)
    },
    {
      onSuccess: (data) => {
        setAdoptionAttempt(data)
      },
    }
  )

  const applyAdoptionContextToAnimal = (animal: AnimalDto, context?: AdoptionContext) => {
    if (!context?.offerType) return
    animal.adoption.offerType = context.offerType
    const priceFromContext = context.offerType === AnimalAdoptionDtoOfferType.Donation ? 0 : context.priceInEuros
    if (context.offerType === AnimalAdoptionDtoOfferType.FreeAmount) {
      animal.adoption.priceInEuros = priceFromContext
    } else if (priceFromContext !== undefined && !Number.isNaN(priceFromContext)) {
      animal.adoption.priceInEuros = priceFromContext
    }
  }

  const ensureAttemptForAnimal = async (
    animalId: string,
    context: AdoptionContext | undefined,
    skipDocuments: boolean
  ) => {
    if (!adopter) return
    const animal = await AnimalsClient.getAnimal(animalId)
    applyAdoptionContextToAnimal(animal, context)
    setAnimal(animal)
    adopter.profileHasBeenAccepted = 'accepted'
    await AdoptersClient.editAccount(adopter)
    await suspendConflictingAdoptionAttempts({
      adopters: adoptersStore.adopters,
      currentAdopterId: adopter.id,
      animalId,
    })
    animal.adoption.adopterId = adopter.id
    animal.adoption.status = AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable
    animal.adoption.step = AnimalAdoptionDtoStep.AdoptionToBeValidated
    animal.adoption.adoptionDate = undefined
    animal.adoption.exitDate = undefined
    animal.adoption.vpaDate = undefined
    await AnimalsClient.editAnimal(animal)

    const existingAttemptId = adoptionAttempt?.id ?? attemptId
    if (!existingAttemptId) {
      const result = await createAdoptionAttempt({
        adopterId: adopter.id,
        animalId,
        shouldPrepareDocuments: !skipDocuments,
        sendEmailToAdopter,
        animal,
        isStripeConnectReady: stripeStatus === 'connected',
        hasDeposit: context?.hasDeposit,
        depositAmount: context?.depositAmount,
        depositDate: context?.depositDate,
        depositStatus: context?.depositStatus,
      })
      setGuestAdoptionLink(result.guestLink ?? null)
      setAdoptionAttempt(result.attempt)
      adopter.adoptionAttempts = [...(adopter.adoptionAttempts ?? []), result.attempt]
      return
    }

    if (context?.hasDeposit && adoptionAttempt) {
      const updatedAttempt = await AdoptersClient.updateAdoptionAttempt(adopter.id, {
        ...adoptionAttempt,
        depositAmount: context.depositAmount,
        depositDate: context.depositDate,
        depositStatus: context.depositStatus,
      })
      setAdoptionAttempt(updatedAttempt)
    }
  }

  /**
   * Creating an adoption can legitimately be refused -- the adopter has already adopted this animal,
   * the organization cannot be paid online, the animal has no price. Without this the rejection was
   * swallowed and the dialog span forever on "Chargement en cours" with nothing said.
   */
  const runOrReport = async (run: () => Promise<void>): Promise<boolean> => {
    try {
      await run()

      return true
    } catch (error) {
      globalSnackbarStore.triggerErrorMessage(readableAxiosError(error).join(' '))
      setIsLoading(false)

      return false
    }
  }

  const submitStep = async (data: {
    animalId?: string
    files?: (File | null)[]
    deliverLater?: boolean
    isLocked?: boolean
    certificateTemplateKey?: string
    contractTemplateKey?: string
    animalPrice?: number
    adoptionContext?: AdoptionContext
  }) => {
    const { animalId, files, deliverLater, isLocked, certificateTemplateKey, contractTemplateKey } = data
    if (!adopter) return
    setIsLoading(true)
    if (currentStep === 1) {
      if (!data.adoptionContext) {
        setIsLoading(false)
        return
      }
      const nextContext = data.adoptionContext
      const nextPrepareDocuments = isPremium ? nextContext.prepareDocuments ?? true : false
      const nextSkipDocuments = !nextPrepareDocuments
      setLocalAdoptionContext(nextContext)
      setPrepareDocuments(nextPrepareDocuments)
      if (providedAnimal) {
        if (!(await runOrReport(() => ensureAttemptForAnimal(providedAnimal.id, nextContext, nextSkipDocuments)))) {
          return
        }
        setCurrentStep(nextSkipDocuments ? 4 : 3)
        setIsLoading(false)
        return
      }
      setCurrentStep(2)
      setIsLoading(false)
      return
    }
    if (currentStep === 2) {
      if (animalId) {
        if (!(await runOrReport(() => ensureAttemptForAnimal(animalId, localAdoptionContext, shouldSkipDocuments)))) {
          return
        }
        const nextStep = shouldSkipDocuments ? 4 : 3
        setCurrentStep(nextStep)
        setIsLoading(false)
        return
      } else {
        setCurrentStep(4)
        return setIsLoading(false)
      }
    }
    if (currentStep === 3) {
      if (files && (adoptionAttempt || attemptId)) {
        try {
          await uploadAdoptionDocumentMutation.mutateAsync({
            file: files[0],
            adopterId: adopter.id,
            adoptionAttemptId: (adoptionAttempt?.id ?? attemptId) as string,
            type: 'certificate',
            deliveredByHand: !files[0],
            pdfTemplateKey: certificateTemplateKey,
          })
          await uploadAdoptionDocumentMutation.mutateAsync({
            file: files[1],
            adopterId: adopter.id,
            adoptionAttemptId: (adoptionAttempt?.id ?? attemptId) as string,
            type: 'contract',
            deliveredByHand: deliverLater ? false : !files[1],
            deliverLater,
            isLocked,
            pdfTemplateKey: contractTemplateKey,
          })
        } catch (error) {
          globalSnackbarStore.triggerErrorMessage(readableAxiosError(error).join(' '))
          setIsLoading(false)
          return
        }
      }
    }
    if (currentStep === 4) {
      const openedAttemptId = adoptionAttempt?.id ?? attemptId
      if (openedAttemptId) {
        window.location.href = `${window.location.origin}/suivi-adoptant/${adopter.id}?tab=ongoing-adoption&attemptId=${openedAttemptId}`
      } else {
        window.location.href = `${window.location.origin}/suivi-adoptant/${adopter?.id}`
      }
      setOpen(false)
      setCurrentStep(getInitialStep())
      setAnimal(providedAnimal)
      setAdoptionAttempt(undefined)
      setGuestAdoptionLink(null)
      setGuestAdoptionLink(null)
      setIsClosing(false)
      return
    }
    setCurrentStep((prev) => prev + 1)
    setIsLoading(false)
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <AdoptionInfoStep
            submitStep={submitStep}
            close={closeDialog}
            isPremium={isPremium}
            defaultValues={adoptionInfoDefaults}
          />
        )
      case 2:
        return <MatchAnimalStep submitStep={submitStep} close={closeDialog} adopterName={adopter?.firstName || ''} />
      case 3:
        return (
          <AddDocumentsStep
            submitStep={submitStep}
            adopter={adopter}
            adoptionAttempt={adoptionAttempt}
            animal={animal}
            setAnimal={setAnimal}
            close={closeDialog}
            prepareDocumentsDefault={prepareDocuments}
            onPrepareDocumentsChange={setPrepareDocuments}
          />
        )
      case 4:
        // A guest adoption is settled from a payment link, so the association is handed the link
        // instead of the procedure email: the adopter already received it.
        if (guestAdoptionLink) {
          return (
            <GuestAdoptionLinkContent
              link={guestAdoptionLink}
              collectedDeposit={localAdoptionContext?.depositStatus ? localAdoptionContext?.depositAmount : undefined}
              onDone={() => submitStep({})}
            />
          )
        }
        return (
          <SummaryStep
            submitStep={submitStep}
            hasAnimal={animal != null}
            close={closeDialog}
            adopterFirstName={adopter?.firstName || ''}
            adopterEmail={adopter?.email || ''}
            animalName={animal?.name || ''}
            customerName={customer?.name || ''}
            price={(animal?.adoption.priceInEuros || 0) + 12}
            animalId={animal?.id || ''}
            adopterId={adopter?.id || ''}
            subscribed={customer?.subscription?.status === 'active'}
            prepareDocuments={prepareDocuments}
          />
        )
      default:
        return null
    }
  }

  return (
    <Dialog onClose={() => console.log('trying to close')} open={open} maxWidth="lg">
      {currentStep >= 3 && stripeStatus === 'loading' ? (
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            padding={3}
            sx={{ width: '100%', height: '100%' }}
          >
            <Typography variant="h4" component="h2" sx={{ fontSize: '20px', color: blue }}>
              Chargement en cours...
            </Typography>
            <CircularProgress sx={{ marginTop: 2 }} />
          </Box>
        </DialogContent>
      ) : currentStep >= 3 && (stripeStatus === 'missing' || stripeStatus === 'error') ? (
        <DialogContent>
          <StripeConnectRequiredStep
            isAdmin={accountsStore.isAdmin()}
            onClose={closeDialog}
            onRefresh={refreshStripeStatus}
          />
        </DialogContent>
      ) : isLoading ? (
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            padding={3}
            sx={{ width: '100%', height: '100%' }}
          >
            <Typography variant="h4" component="h2" sx={{ fontSize: '20px', color: blue }}>
              Chargement en cours...
            </Typography>
            <CircularProgress sx={{ marginTop: 2 }} />
          </Box>
        </DialogContent>
      ) : isClosing ? (
        /* Alert user that adopter will be deleted and animal if applicable will be unassigned */
        <>
          <DialogContent>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              padding={3}
              sx={{ width: '100%', height: '100%' }}
            >
              <Typography variant="h4" component="h2" sx={{ fontSize: '20px', color: blue }}>
                Êtes-vous sûr de vouloir quitter ?
              </Typography>
              <Typography>{`Si vous continuez, ${
                noAdopterDelete ? '' : `l'adoptant sera supprimé et `
              } l'animal restera adoptable`}</Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="contained" onClick={() => setIsClosing(false)}>
              Annuler
            </Button>
            <Button variant="contained" onClick={() => deleteAdopterAndCloseDialog()}>
              Confirmer
            </Button>
          </DialogActions>
        </>
      ) : (
        renderStep()
      )}
    </Dialog>
  )
}
